import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsList from "@/components/CardsList";

import {PlayerAPI} from "@/api/player";

import AddIcon from "@mui/icons-material/Add";

import {ERROR_OPERATION, AREA_OPTIONS} from "@/utils/constants";
import {getBirthYears} from "@/utils/functions";

import "./index.scss";
import {useTranslation} from "react-i18next";

const ITEMS_PER_PAGE = 24;

const STORAGE_KEY = "manager_player_filters";

function ManagerPlayersPage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);
	const {t} = useTranslation();

	const [loading, setLoading] = useState(false);
	const [filtersData, setFiltersData] = useState(
		JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {},
	);
	// Solo la ricerca testuale è "debounced"; gli altri filtri, la paginazione
	// e l'ordinamento fetchano subito.
	const [searchInput, setSearchInput] = useState(
		JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.search || "",
	);

	const [players, setPlayers] = useState([]);
	const [totalItems, setTotalItems] = useState();
	const [currentPage, setCurrentPage] = useState(1);
	const [currentSorting, setCurrentSorting] = useState();
	const [currentSortingOrder, setCurrentSortingOrder] = useState();

	const updateFilter = (key, value) => {
		const updated = {...filtersData, [key]: value};
		if (value === undefined || value === null || value === "") {
			delete updated[key];
		}
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
		setFiltersData(updated);
		setCurrentPage(1);
	};

	// Commit debounced della sola ricerca testuale
	useEffect(() => {
		const timer = setTimeout(() => {
			if ((searchInput || "") !== (filtersData?.search || "")) {
				updateFilter("search", searchInput || undefined);
			}
		}, 500);
		return () => clearTimeout(timer);
	}, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

	const filters = [
		{
			key: "search",
			label: "Giocatore",
			value: searchInput,
			setValue: (value) => setSearchInput(value),
		},
		{
			key: "birthYear",
			label: "Anno di nascita",
			type: "select",
			options: getBirthYears(),
			sorted: false,
			value: filtersData?.birthYear,
			setValue: (value) => updateFilter("birthYear", value),
		},
		{
			key: "role",
			label: "Ruolo",
			type: "select",
			options: AREA_OPTIONS,
			sorted: false,
			value: filtersData?.role,
			setValue: (value) => updateFilter("role", value),
		},
	];

	const getPlayers = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.paginated({
				page: currentPage,
				pageSize: ITEMS_PER_PAGE,
				prospect: false,
				seasonId,
				sorting: currentSorting,
				descending: currentSortingOrder === "descend",
				search: filtersData?.search || undefined,
				birthYear: filtersData?.birthYear?.value,
				role: filtersData?.role?.label,
			});
			setPlayers(response?.data || []);
			setTotalItems(response?.totalRecords || 0);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getPlayers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filtersData, currentPage, currentSorting, currentSortingOrder, seasonId]);

	const handlePageChanged = (page) => {
		setCurrentPage(page);
	};

	const fromOptionToSorting = (sorting) => {
		switch (sorting) {
			case "firstName":
				return 2;
			case "lastName":
				return 1;
			case "dateOfBirth":
				return 3;
			default:
				return;
		}
	};

	const playersListConfiguration = {
		data: players,
		showRoster: true,
		onClick: (player) => navigate("/players/" + player.id),
	};

	const mainActions = [
		{
			icon: <AddIcon />,
			title: "Aggiungi giocatore",
			onClick: () => navigate("/players/new"),
		},
	];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				current="Giocatori"
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="players-list-container">
				<CardsList
					breadcrumbs
					filtersData={filters}
					type="roster_player"
					listConfiguration={playersListConfiguration}
					sortOptions={[
						{key: "firstName", label: t("name")},
						{
							key: "lastName",
							label: "Cognome",
							sortedByDefault: true,
							sortedOrderByDefault: "ascend",
						},
						{key: "dateOfBirth", label: "Data di nascita"},
					]}
					handleSortChange={(option, order) => {
						setCurrentSorting(fromOptionToSorting(option));
						setCurrentSortingOrder(order);
					}}
					totalItems={totalItems}
					itemsPerPage={ITEMS_PER_PAGE}
					handlePageChanged={handlePageChanged}
				/>
			</div>
		</Page>
	);
}

export default ManagerPlayersPage;
